import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, ArrowUp, ArrowDown, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { jobService } from '@/services/storage';
import type { Job } from '@/types';
import { toast } from 'sonner';

export default function AdminJobs() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    department: '',
    location: '',
    type: '全职',
    experience: '',
    description: '',
    requirements: [''],
    sortOrder: 1,
    isActive: true,
  });

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    setJobs(await jobService.getAll());
  };

  const handleAdd = async () => {
    setEditingJob(null);
    setFormData({
      title: '',
      department: '',
      location: '',
      type: '全职',
      experience: '',
      description: '',
      requirements: [''],
      sortOrder: jobs.length + 1,
      isActive: true,
    });
    setDialogOpen(true);
  };

  const handleEdit = async (job: Job) => {
    setEditingJob(job);
    setFormData({
      title: job.title,
      department: job.department,
      location: job.location,
      type: job.type,
      experience: job.experience,
      description: job.description,
      requirements: job.requirements,
      sortOrder: job.sortOrder,
      isActive: job.isActive,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('确定要删除此职位吗？')) {
      await jobService.delete(id);
      toast.success('职位已删除');
      loadJobs();
    }
  };

  const handleToggleActive = async (job: Job) => {
    await jobService.update(job.id, { isActive: !job.isActive });
    toast.success(job.isActive ? '职位已隐藏' : '职位已显示');
    loadJobs();
  };

  const handleMoveUp = async (job: Job) => {
    const sorted = [...jobs].sort((a, b) => a.sortOrder - b.sortOrder);
    const index = sorted.findIndex(j => j.id === job.id);
    if (index > 0) {
      const prevJob = sorted[index - 1];
      await jobService.update(job.id, { sortOrder: prevJob.sortOrder });
      await jobService.update(prevJob.id, { sortOrder: job.sortOrder });
      loadJobs();
    }
  };

  const handleMoveDown = async (job: Job) => {
    const sorted = [...jobs].sort((a, b) => a.sortOrder - b.sortOrder);
    const index = sorted.findIndex(j => j.id === job.id);
    if (index < sorted.length - 1) {
      const nextJob = sorted[index + 1];
      await jobService.update(job.id, { sortOrder: nextJob.sortOrder });
      await jobService.update(nextJob.id, { sortOrder: job.sortOrder });
      loadJobs();
    }
  };

  const addRequirement = () => {
    setFormData({
      ...formData,
      requirements: [...formData.requirements, ''],
    });
  };

  const updateRequirement = (index: number, value: string) => {
    const newRequirements = [...formData.requirements];
    newRequirements[index] = value;
    setFormData({ ...formData, requirements: newRequirements });
  };

  const removeRequirement = (index: number) => {
    const newRequirements = formData.requirements.filter((_, i) => i !== index);
    setFormData({ ...formData, requirements: newRequirements });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !formData.department) {
      toast.error('请填写必填项');
      return;
    }

    const filteredRequirements = formData.requirements.filter(r => r.trim() !== '');

    if (editingJob) {
      await jobService.update(editingJob.id, { ...formData, requirements: filteredRequirements });
      toast.success('职位已更新');
    } else {
      const newJob: Job = {
        id: Date.now().toString(),
        ...formData,
        requirements: filteredRequirements,
      };
      await jobService.add(newJob);
      toast.success('职位已添加');
    }

    setDialogOpen(false);
    loadJobs();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">招聘职位管理</h1>
        <Button onClick={handleAdd}>
          <Plus className="mr-2 h-5 w-5" />
          添加职位
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>职位名称</TableHead>
                <TableHead>部门</TableHead>
                <TableHead>地点</TableHead>
                <TableHead>经验</TableHead>
                <TableHead>状态</TableHead>
                <TableHead className="text-right">操作</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id} className={!job.isActive ? 'opacity-50' : ''}>
                  <TableCell className="font-medium">{job.title}</TableCell>
                  <TableCell>{job.department}</TableCell>
                  <TableCell>{job.location}</TableCell>
                  <TableCell>{job.experience}</TableCell>
                  <TableCell>
                    <Badge variant={job.isActive ? 'default' : 'secondary'}>
                      {job.isActive ? '显示中' : '已隐藏'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => handleMoveUp(job)}>
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleMoveDown(job)}>
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleToggleActive(job)}>
                        {job.isActive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(job)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(job.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {jobs.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              暂无职位数据
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingJob ? '编辑职位' : '添加职位'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">
                  职位名称 <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="如模拟IC设计工程师"
                  required
                />
              </div>
              <div>
                <Label htmlFor="department">
                  部门 <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="department"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  placeholder="如研发部"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="location">工作地点</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="如无锡"
                />
              </div>
              <div>
                <Label htmlFor="type">工作类型</Label>
                <select
                  id="type"
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="全职">全职</option>
                  <option value="兼职">兼职</option>
                  <option value="实习">实习</option>
                </select>
              </div>
              <div>
                <Label htmlFor="experience">经验要求</Label>
                <Input
                  id="experience"
                  value={formData.experience}
                  onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                  placeholder="如3-5年"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="sortOrder">排序号</Label>
              <Input
                id="sortOrder"
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <Label htmlFor="description">职位描述</Label>
              <textarea
                id="description"
                className="w-full border rounded-md px-3 py-2"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="请描述该职位的主要工作内容"
              />
            </div>
            <div>
              <Label>任职要求</Label>
              <div className="space-y-2 mt-2">
                {formData.requirements.map((req, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <Input
                      value={req}
                      onChange={(e) => updateRequirement(index, e.target.value)}
                      placeholder={`要求 ${index + 1}`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRequirement(index)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addRequirement}>
                  <Plus className="mr-1 h-4 w-4" />
                  添加要求
                </Button>
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                取消
              </Button>
              <Button type="submit">
                <Save className="mr-2 h-4 w-4" />
                保存
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
